import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import {GLTFLoader}      from 'three/examples/jsm/loaders/GLTFLoader.js'
import * as dat from 'lil-gui'


THREE.ColorManagement.enabled = false

/**
 * Base
 */
// Debug
const gui = new dat.GUI()
gui.hide();

// Canvas
const canvas = document.querySelector('canvas.webgl')

// Scene
const scene = new THREE.Scene()

/**
 * Textures
 */
// add manager
const loadingManager = new THREE.LoadingManager()
// add texture loader
const textureLoader = new THREE.TextureLoader(loadingManager)
const cubeTextureLoader = new THREE.CubeTextureLoader() // loader for environments
// load matcaps
const redCoreTexture = textureLoader.load('textures/matcaps/red.png')
// load environments
const environmentMapTexture2 = cubeTextureLoader.load([
    '/textures/environmentMaps/2/px.jpg',
    '/textures/environmentMaps/2/nx.jpg',
    '/textures/environmentMaps/2/py.jpg',
    '/textures/environmentMaps/2/ny.jpg',
    '/textures/environmentMaps/2/pz.jpg',
    '/textures/environmentMaps/2/nz.jpg'
])

/**
 * Objects
 */

// Zeppelins
const metalMaterial = new THREE.MeshStandardMaterial({
    color: 'white', 
    metalness: 1, 
    roughness: 0, 
    envMap: environmentMapTexture2})

const zeppelins = []
for(let i=0; i<400; i++)
{
    const object = new THREE.Mesh(
        new THREE.SphereGeometry(0.5, 32, 16),
        metalMaterial
    )
    object.scale.x = 2.5
    object.rotation.x = Math.random() * Math.PI
    object.rotation.y = Math.random() * Math.PI
    scene.add(object)
    zeppelins.push(object)
}

// Core
const coreMaterial = new THREE.MeshMatcapMaterial({matcap: redCoreTexture})
const spheresSpread = 0.9
const spheres = []
for(let i=0; i<10; i++)
{
    const object = new THREE.Mesh(
        new THREE.SphereGeometry(0.5, 16, 16),
        coreMaterial
    )
    object.position.x = (Math.random()-0.5)*spheresSpread
    object.position.y = (Math.random()-0.5)*spheresSpread
    object.position.z = (Math.random()-0.5)*spheresSpread
    object.scale.x = 1.2
    scene.add(object)
    spheres.push(object)
}

/**
 * Stars
 */
// geometry
const particlesCount = 2000
const particlesSpread = 100
const positions = new Float32Array(particlesCount * 3)
for(let i = 0; i < 2000; i++)
{
    positions[i * 3 + 0] = (Math.random() - 0.5) * particlesSpread 
    positions[i * 3 + 1] = (Math.random() - 0.5) * particlesSpread 
    positions[i * 3 + 2] = (Math.random() - 0.5) * particlesSpread 
}
const particlesGeometry = new THREE.BufferGeometry()
particlesGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3))
// material
const particlesMaterial = new THREE.PointsMaterial({
    sizeAttenuation: true,
    size: 0.03
})
// Points
const particles = new THREE.Points(particlesGeometry, particlesMaterial)
scene.add(particles)

/**
 * Raycaster
 */
const raycaster = new THREE.Raycaster()

/**
 * Sizes
 */
const sizes = {
    width: window.innerWidth,
    height: window.innerHeight
}

window.addEventListener('resize', () =>
{
    // Update sizes
    sizes.width = window.innerWidth
    sizes.height = window.innerHeight

    // Update camera
    camera.aspect = sizes.width / sizes.height
    camera.updateProjectionMatrix()

    // Update renderer
    renderer.setSize(sizes.width, sizes.height)
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
})

/**
 * Mouse
 */
const mouse = new THREE.Vector2()
window.addEventListener // callback functions with 2 parameters: name of the event. second: function
    (
        'mousemove', // first parameter 
        (event) =>   // add variable with lots of information 
        {            // second parameter
            mouse.x = event.clientX / sizes.width * 2 - 1
            mouse.y = -(event.clientY / sizes.height * 2 - 1)
        }
    )

/**
 * Camera
 */
// Base camera
const camera = new THREE.PerspectiveCamera(75, sizes.width / sizes.height, 0.1, 100)
camera.position.z = 3
scene.add(camera)

// Controls
const controls = new OrbitControls(camera, canvas)
controls.enableDamping = true

/**
 * Renderer
 */
const renderer = new THREE.WebGLRenderer({
    canvas: canvas
})
renderer.outputColorSpace = THREE.LinearSRGBColorSpace
renderer.setSize(sizes.width, sizes.height)
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
renderer.setClearColor('#101015')

/**
 * Animate
 */
const clock = new THREE.Clock()
let oldElapsedTime = 0

// scale speed

const tick = () =>
{
    const elapsedTime = clock.getElapsedTime()
    const deltaTime = elapsedTime - oldElapsedTime
    oldElapsedTime = elapsedTime
    const rotationSpeed = deltaTime * 0.7 // 0.5 is good

    // Update objects
    raycaster.setFromCamera(mouse, camera)
    const zeppelinIntersects = raycaster.intersectObjects(zeppelins) // get intersects
    for(const zeppelinIntersect of zeppelinIntersects){
        zeppelinIntersect.object.rotation.z += rotationSpeed
    }
    const spheresIntersects = raycaster.intersectObjects(spheres)
    for(const spheresIntersect of spheresIntersects){
        spheresIntersect.object.rotation.y += rotationSpeed
        spheresIntersect.object.rotation.z += rotationSpeed
    }
    for(const object of spheres){
        object.rotation.y += rotationSpeed
        object.rotation.z += rotationSpeed}

    // Update stars
    particles.rotation.x = elapsedTime * 0.01
    particles.rotation.y = - elapsedTime * 0.02

    // Update controls
    controls.update()

    // Render
    renderer.render(scene, camera)

    // Call tick again on the next frame
    window.requestAnimationFrame(tick)
}

tick()